import { userInviteBodySchema, type UserInviteBody } from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { inviteUser, usersQueryKeys } from './api.js';

export interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * « Inviter » — `02` §13 : « inviter (email + nom → email d'invitation) ».
 * Déclenché depuis un bouton de la page (état local), pas une route — voir
 * le contrat de la vague (aucun sous-écran pour ce dialogue).
 */
export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<UserInviteBody>({ resolver: zodResolver(userInviteBodySchema) });

  const mutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: (result) => {
      toast.success(`Invitation envoyée à ${result.email}.`);
      void queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      reset();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      setError('root', {
        message: error instanceof ApiError ? error.message : 'Une erreur technique est survenue.',
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Inviter un utilisateur</DialogTitle>
          <DialogDescription>
            Un email d’invitation avec un lien de création de mot de passe sera envoyé à l’adresse
            indiquée.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(event) => {
            void handleSubmit((values) => {
              mutation.mutate(values);
            })(event);
          }}
        >
          <Field label="Email" htmlFor="invite-email" error={errors.email?.message}>
            <Input
              id="invite-email"
              type="email"
              autoComplete="email"
              aria-invalid={errors.email ? true : undefined}
              {...register('email')}
            />
          </Field>
          <Field label="Nom" htmlFor="invite-name" error={errors.name?.message}>
            <Input
              id="invite-name"
              type="text"
              autoComplete="name"
              aria-invalid={errors.name ? true : undefined}
              {...register('name')}
            />
          </Field>
          {errors.root ? (
            <p
              role="alert"
              className="rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg"
            >
              {errors.root.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="submit" isLoading={isSubmitting || mutation.isPending}>
              Envoyer l’invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
